import { useEffect, useState } from 'react';
import { apiFetch, ApiError } from '../../lib/apiClient';

/**
 * Detection-Rule Effectiveness scorecard.
 *
 * Lazy-loaded after the parent report renders. Shows:
 *   1. KPI strip (rules fired, triaged %, fleet precision, rules to tune)
 *   2. "Highest-Value Rules" — high precision + meaningful volume
 *   3. "Noisiest Rules — Recommended Tuning" — low precision + AI-generated
 *      tuning suggestion per rule
 *
 * Uses the same lazy-load + state-machine pattern as ExecutiveSummary,
 * KillChainStories, and the other AI-augmented sections.
 */

// ── Types (mirror artifacts/api-server/src/lib/ruleEffectiveness.ts) ──────

export interface RuleStats {
  ruleName: string;
  totalIncidents: number;
  open: number;
  truePositive: number;
  falsePositive: number;
  benignPositive: number;
  undetermined: number;
  highSeverity: number;
  mediumSeverity: number;
  avgMttrMinutes: number | null;
  firstFiredUtc: string;
  lastFiredUtc: string;
  precisionPct: number | null;
  noisePct: number | null;
  needsTuning: boolean;
  tuningSuggestion: string;
  tuningSuggestionError: string | null;
}

export interface RuleEffectivenessScorecard {
  rulesFired: number;
  totalIncidents: number;
  triagedIncidents: number;
  fleetPrecisionPct: number | null;
  rulesNeedingTuning: number;
}

interface ApiResponse {
  customerId: string;
  period: 'weekly' | 'monthly';
  timezone: string;
  scorecard: RuleEffectivenessScorecard;
  rules: RuleStats[];
  topValueRules: RuleStats[];
  topNoisyRules: RuleStats[];
  errors: { rules: boolean };
  generatedAt: string;
}

type Period = 'weekly' | 'monthly';

interface Props {
  customerId: string;
  period: Period;
  weekEnding?: string;
  month?: string;
  cacheKey?: string;
}

// ── Helpers ────────────────────────────────────────────────────────────────

function fmtDuration(min: number | null): string {
  if (min === null || !Number.isFinite(min)) return ' - ';
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h < 24) return m === 0 ? `${h}h` : `${h}h ${m}m`;
  const d = Math.floor(h / 24);
  const rh = h % 24;
  return rh === 0 ? `${d}d` : `${d}d ${rh}h`;
}

function fmtPct(p: number | null): string {
  if (p === null || !Number.isFinite(p)) return ' - ';
  return `${p.toFixed(1)}%`;
}

function precisionTone(p: number | null): { bg: string; fg: string; label: string } {
  if (p === null) return { bg: '#f1f5f9', fg: '#475569', label: 'Unrated' };
  if (p >= 80) return { bg: '#dcfce7', fg: '#166534', label: 'Excellent' };
  if (p >= 60) return { bg: '#d1fae5', fg: '#065f46', label: 'Good' };
  if (p >= 40) return { bg: '#fef3c7', fg: '#854d0e', label: 'Fair' };
  if (p >= 20) return { bg: '#fed7aa', fg: '#9a3412', label: 'Poor' };
  return { bg: '#fee2e2', fg: '#991b1b', label: 'Critical' };
}

// ── Component ──────────────────────────────────────────────────────────────

export function RuleEffectiveness({ customerId, period, weekEnding, month, cacheKey }: Props) {
  const [state, setState] = useState<
    | { kind: 'idle' }
    | { kind: 'loading' }
    | { kind: 'ready'; data: ApiResponse }
    | { kind: 'unavailable'; reason: string }
  >({ kind: 'idle' });

  useEffect(() => {
    if (!customerId) { setState({ kind: 'idle' }); return; }
    if (period === 'weekly' && !weekEnding) { setState({ kind: 'idle' }); return; }
    if (period === 'monthly' && !month) { setState({ kind: 'idle' }); return; }

    const ctrl = new AbortController();
    let cancelled = false;
    setState({ kind: 'loading' });

    const params = new URLSearchParams({ customerId, period });
    if (period === 'weekly' && weekEnding) params.set('weekEnding', weekEnding);
    if (period === 'monthly' && month) params.set('month', month);

    apiFetch<ApiResponse>(`/rule-effectiveness?${params.toString()}`, { signal: ctrl.signal })
      .then((r) => { if (!cancelled) setState({ kind: 'ready', data: r }); })
      .catch((e: ApiError | Error) => {
        if (cancelled) return;
        if (e instanceof ApiError && e.notAuthenticated) { setState({ kind: 'idle' }); return; }
        const reason = e instanceof Error ? e.message : String(e);
        setState({ kind: 'unavailable', reason });
      });

    return () => { cancelled = true; ctrl.abort(); };
  }, [customerId, period, weekEnding, month, cacheKey]);

  if (state.kind === 'idle') return null;

  if (state.kind === 'loading') {
    return (
      <div
        className="re-loading"
        style={{
          background: '#f8fafc',
          border: '1px dashed #cbd5e1',
          borderRadius: 12,
          padding: '20px 24px',
          color: '#475569',
          fontSize: '0.9em',
        }}
        aria-busy="true"
        aria-live="polite"
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <Spinner />
          <strong style={{ color: '#0f172a' }}>Scoring detection-rule effectiveness…</strong>
        </div>
        <div style={{ fontSize: '0.85em' }}>
          Aggregating per-rule classifications and asking the AI for tuning recommendations on the noisiest rules.
        </div>
      </div>
    );
  }

  if (state.kind === 'unavailable') {
    return (
      <div
        style={{
          background: '#fff7ed',
          border: '1px solid #fed7aa',
          borderRadius: 12,
          padding: '12px 18px',
          color: '#9a3412',
          fontSize: '0.85em',
        }}
      >
        <strong>Rule-effectiveness scoring unavailable.</strong>{' '}
        <span style={{ color: '#7c2d12' }}>{state.reason}</span>{' '}
        The full report below is unaffected.
      </div>
    );
  }

  const { scorecard, topValueRules, topNoisyRules, errors } = state.data;

  if (errors.rules && scorecard.rulesFired === 0) {
    return (
      <div
        style={{
          background: '#fff7ed',
          border: '1px solid #fed7aa',
          borderRadius: 12,
          padding: '12px 18px',
          color: '#9a3412',
          fontSize: '0.85em',
        }}
      >
        <strong>Rule data unavailable.</strong>{' '}
        We couldn't pull incident data for this period. Check the partial-data banner above for the underlying cause.
      </div>
    );
  }

  if (scorecard.rulesFired === 0) {
    return (
      <div
        style={{
          background: '#f0fdf4',
          border: '1px solid #86efac',
          borderRadius: 12,
          padding: '16px 20px',
          color: '#166534',
          fontSize: '0.9em',
        }}
      >
        <strong>No rules fired this {period === 'weekly' ? 'week' : 'month'}.</strong>{' '}
        That can be a sign of a quiet period - or that detections need broader coverage. Consider scheduling a rule-coverage review.
      </div>
    );
  }

  const triagedRate = scorecard.totalIncidents > 0
    ? Math.round((scorecard.triagedIncidents / scorecard.totalIncidents) * 100)
    : 0;

  return (
    <div className="re-root">
      <ScopedStyles />

      {/* ── Scorecard KPI strip ───────────────────────────────────── */}
      <div className="re-scorecard">
        <KpiTile
          label="Rules fired"
          value={scorecard.rulesFired.toString()}
          subtitle={`${scorecard.totalIncidents.toLocaleString()} incidents`}
          accent="#0f172a"
        />
        <KpiTile
          label="Triaged"
          value={`${triagedRate}%`}
          subtitle={`${scorecard.triagedIncidents.toLocaleString()} of ${scorecard.totalIncidents.toLocaleString()}`}
          accent={triagedRate >= 75 ? '#16a34a' : triagedRate >= 40 ? '#d97706' : '#dc2626'}
          hint="Share of incidents an analyst classified (TP / FP / Benign)."
        />
        <KpiTile
          label="Fleet precision"
          value={fmtPct(scorecard.fleetPrecisionPct)}
          subtitle="True-positive rate"
          accent={
            scorecard.fleetPrecisionPct === null ? '#475569'
            : scorecard.fleetPrecisionPct >= 60 ? '#16a34a'
            : scorecard.fleetPrecisionPct >= 30 ? '#d97706' : '#dc2626'
          }
          hint="Across all rules: TP ÷ (TP + FP + Benign)."
        />
        <KpiTile
          label="Rules to tune"
          value={scorecard.rulesNeedingTuning.toString()}
          subtitle="Heuristic flag"
          accent={scorecard.rulesNeedingTuning === 0 ? '#16a34a' : '#dc2626'}
          hint="Rules with ≥3 triaged firings, ≥70% noise, and ≤1 true positive."
        />
      </div>

      {/* ── Top "value" rules ─────────────────────────────────────── */}
      <RuleTable
        title="Highest-value detections"
        emoji="✓"
        accent="#16a34a"
        emptyMessage="No detections crossed the value threshold this period - usually means too few incidents have been classified yet."
        rules={topValueRules}
        showSuggestion={false}
      />

      {/* ── Noisiest rules + AI tuning recommendations ────────────── */}
      <RuleTable
        title="Noisiest rules - tuning recommended"
        emoji="!"
        accent="#dc2626"
        emptyMessage="No rules crossed the noise threshold. Detection portfolio is well-tuned for this period."
        rules={topNoisyRules}
        showSuggestion
      />
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────

function KpiTile({ label, value, subtitle, accent, hint }:
  { label: string; value: string; subtitle: string; accent: string; hint?: string }) {
  return (
    <div className="re-kpi" title={hint}>
      <div className="re-kpi-label">{label}</div>
      <div className="re-kpi-value" style={{ color: accent }}>{value}</div>
      <div className="re-kpi-sub">{subtitle}</div>
    </div>
  );
}

function RuleTable({ title, emoji, accent, emptyMessage, rules, showSuggestion }:
  {
    title: string;
    emoji: string;
    accent: string;
    emptyMessage: string;
    rules: RuleStats[];
    showSuggestion: boolean;
  }
) {
  return (
    <div className="re-section" style={{ borderLeftColor: accent }}>
      <div className="re-section-header">
        <span className="re-section-icon" style={{ background: accent }}>{emoji}</span>
        <h4 className="re-section-title">{title}</h4>
        <span className="re-section-count">{rules.length}</span>
      </div>
      {rules.length === 0 ? (
        <div className="re-empty">{emptyMessage}</div>
      ) : (
        <div className="re-rule-list">
          {rules.map((r) => <RuleRow key={r.ruleName} rule={r} showSuggestion={showSuggestion} />)}
        </div>
      )}
    </div>
  );
}

function RuleRow({ rule, showSuggestion }: { rule: RuleStats; showSuggestion: boolean }) {
  const tone = precisionTone(rule.precisionPct);
  return (
    <div className="re-rule">
      <div className="re-rule-head">
        <div className="re-rule-name">{rule.ruleName}</div>
        <div className="re-rule-precision">
          <span className="re-rule-precision-pill" style={{ background: tone.bg, color: tone.fg }}>
            {tone.label}
          </span>
          <span className="re-rule-precision-num">{fmtPct(rule.precisionPct)}</span>
          <span className="re-rule-precision-label">precision</span>
        </div>
      </div>

      {/* Classification bar (only meaningful when triaged > 0) */}
      <ClassificationBar rule={rule} />

      <div className="re-rule-stats">
        <Stat label="Incidents" value={rule.totalIncidents.toLocaleString()} />
        <Stat label="True+" value={rule.truePositive.toLocaleString()} tone="green" />
        <Stat label="False+" value={rule.falsePositive.toLocaleString()} tone="red" />
        <Stat label="Benign" value={rule.benignPositive.toLocaleString()} tone="blue" />
        {rule.undetermined > 0 && (
          <Stat label="Untriaged" value={rule.undetermined.toLocaleString()} tone="amber" />
        )}
        <Stat label="Avg MTTR" value={fmtDuration(rule.avgMttrMinutes)} />
        {rule.highSeverity > 0 && (
          <Stat label="High sev" value={rule.highSeverity.toLocaleString()} tone="red" />
        )}
      </div>

      {showSuggestion && (
        <TuningCallout suggestion={rule.tuningSuggestion} error={rule.tuningSuggestionError} />
      )}
    </div>
  );
}

function ClassificationBar({ rule }: { rule: RuleStats }) {
  const triaged = rule.truePositive + rule.falsePositive + rule.benignPositive;
  if (triaged === 0) {
    return (
      <div className="re-cls-empty">
        Not yet classified - analysts need to triage these incidents before precision can be measured.
      </div>
    );
  }
  const tp = (rule.truePositive / triaged) * 100;
  const fp = (rule.falsePositive / triaged) * 100;
  const bp = (rule.benignPositive / triaged) * 100;
  return (
    <div
      className="re-cls-bar"
      role="img"
      aria-label={`Classification breakdown: ${rule.truePositive} true positive, ${rule.falsePositive} false positive, ${rule.benignPositive} benign positive`}
    >
      {tp > 0 && (
        <div className="re-cls-seg re-cls-tp" style={{ width: `${tp}%` }} title={`True positive: ${rule.truePositive} (${tp.toFixed(1)}%)`}>
          {tp >= 12 && <span>{rule.truePositive}</span>}
        </div>
      )}
      {fp > 0 && (
        <div className="re-cls-seg re-cls-fp" style={{ width: `${fp}%` }} title={`False positive: ${rule.falsePositive} (${fp.toFixed(1)}%)`}>
          {fp >= 12 && <span>{rule.falsePositive}</span>}
        </div>
      )}
      {bp > 0 && (
        <div className="re-cls-seg re-cls-bp" style={{ width: `${bp}%` }} title={`Benign positive: ${rule.benignPositive} (${bp.toFixed(1)}%)`}>
          {bp >= 12 && <span>{rule.benignPositive}</span>}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: 'green' | 'red' | 'blue' | 'amber' }) {
  const color =
    tone === 'green' ? '#16a34a' :
    tone === 'red' ? '#dc2626' :
    tone === 'blue' ? '#2563eb' :
    tone === 'amber' ? '#d97706' :
    '#0f172a';
  return (
    <div className="re-stat">
      <div className="re-stat-label">{label}</div>
      <div className="re-stat-value" style={{ color }}>{value}</div>
    </div>
  );
}

function TuningCallout({ suggestion, error }: { suggestion: string; error: string | null }) {
  if (suggestion) {
    return (
      <div className="re-tuning">
        <div className="re-tuning-header">
          <SparkleIcon /> <span>AI tuning recommendation</span>
        </div>
        <div className="re-tuning-body">{suggestion}</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="re-tuning re-tuning-err">
        AI suggestion unavailable - {error.toLowerCase().replace(/\.$/, '')}. Stats above still indicate this rule needs review.
      </div>
    );
  }
  return null;
}

function Spinner() {
  return (
    <span
      style={{
        display: 'inline-block',
        width: 14,
        height: 14,
        border: '2px solid #cbd5e1',
        borderTopColor: '#0f172a',
        borderRadius: '50%',
        animation: 're-spin 0.8s linear infinite',
      }}
    >
      <style>{`@keyframes re-spin { to { transform: rotate(360deg); } }`}</style>
    </span>
  );
}

function SparkleIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M8 1l1.5 4.5L14 7l-4.5 1.5L8 13l-1.5-4.5L2 7l4.5-1.5L8 1z" />
    </svg>
  );
}

// All visual styling scoped under `.re-root` so it cannot bleed into other
// report sections.
function ScopedStyles() {
  return (
    <style>{`
.re-root { display: flex; flex-direction: column; gap: 16px; }

.re-scorecard {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}
.re-kpi {
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px 16px;
  text-align: center;
  box-shadow: 0 2px 6px rgba(15,23,42,0.04);
}
.re-kpi-label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  color: #64748b;
  font-weight: 700;
  margin-bottom: 4px;
}
.re-kpi-value {
  font-size: 1.7em;
  font-weight: 800;
  font-family: ui-monospace, monospace;
  line-height: 1.1;
}
.re-kpi-sub {
  font-size: 11px;
  color: #475569;
  margin-top: 4px;
}

.re-section {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-left: 4px solid;
  border-radius: 12px;
  padding: 16px 20px;
  box-shadow: 0 2px 8px rgba(15,23,42,0.04);
}
.re-section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}
.re-section-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  color: #fff;
  font-weight: 800;
  font-size: 13px;
  flex-shrink: 0;
}
.re-section-title {
  margin: 0;
  font-size: 1.05em;
  color: #0f172a;
  font-weight: 700;
  flex: 1;
}
.re-section-count {
  font-family: ui-monospace, monospace;
  background: #f1f5f9;
  color: #475569;
  font-size: 12px;
  font-weight: 700;
  padding: 2px 10px;
  border-radius: 999px;
}
.re-empty {
  padding: 12px 14px;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  color: #475569;
  font-size: 0.88em;
  font-style: italic;
}

.re-rule-list { display: flex; flex-direction: column; gap: 12px; }

.re-rule {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px 14px;
}
.re-rule-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}
.re-rule-name {
  font-weight: 700;
  color: #0f172a;
  font-size: 0.95em;
  line-height: 1.35;
  flex: 1 1 240px;
  word-wrap: break-word;
  min-width: 0;
}
.re-rule-precision {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  flex-wrap: nowrap;
  white-space: nowrap;
}
.re-rule-precision-pill {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 8px;
  border-radius: 999px;
}
.re-rule-precision-num {
  font-family: ui-monospace, monospace;
  font-weight: 800;
  font-size: 1.05em;
  color: #0f172a;
}
.re-rule-precision-label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #64748b;
  font-weight: 600;
}

.re-cls-bar {
  display: flex;
  height: 18px;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 10px;
  background: #f1f5f9;
}
.re-cls-seg {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
  color: #fff;
  font-family: ui-monospace, monospace;
}
.re-cls-tp { background: #16a34a; }
.re-cls-fp { background: #dc2626; }
.re-cls-bp { background: #2563eb; }
.re-cls-empty {
  font-size: 11px;
  color: #64748b;
  font-style: italic;
  padding: 6px 8px;
  background: #f1f5f9;
  border-radius: 4px;
  margin-bottom: 10px;
}

.re-rule-stats {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  padding: 8px 0 4px;
}
.re-stat { min-width: 50px; }
.re-stat-label {
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #64748b;
  font-weight: 700;
}
.re-stat-value {
  font-size: 0.95em;
  font-weight: 700;
  font-family: ui-monospace, monospace;
  margin-top: 1px;
}

.re-tuning {
  background: #faf5ff;
  border: 1px solid #e9d5ff;
  border-radius: 8px;
  padding: 10px 14px;
  margin-top: 10px;
  color: #1e1b4b;
  font-size: 0.88em;
  line-height: 1.45;
}
.re-tuning-header {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6d28d9;
  margin-bottom: 4px;
}
.re-tuning-body { font-weight: 500; }

.re-tuning-err {
  background: #fff7ed;
  border-color: #fed7aa;
  color: #9a3412;
  font-size: 0.82em;
  font-style: italic;
}

@media (max-width: 720px) {
  .re-scorecard { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .re-rule-stats { gap: 12px; }
}

@media print {
  .re-section, .re-rule { break-inside: avoid; box-shadow: none; }
  .re-tuning { background: #fff; }
}
    `}</style>
  );
}
